import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { LanguageProvider } from "./context/LanguageContext";
import SmoothScroll from "./components/SmoothScroll";
import PublicLayout from "./components/PublicLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import NavGuard from "./components/NavGuard";
import { InstallProvider } from "./context/InstallContext";

import Home from "./pages/Home";
import HowItWorks from "./pages/HowItWorks";
import Weekly from "./pages/Weekly";
import Gallery from "./pages/Gallery";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Legal from "./pages/Legal";

import AppLayout from "./pages/app/AppLayout";
import Dashboard from "./pages/app/Dashboard";
import SubmitIdea from "./pages/app/SubmitIdea";
import MyProjects from "./pages/app/MyProjects";
import ProjectDetail from "./pages/app/ProjectDetail";
import Profile from "./pages/app/Profile";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminUserProjects from "./pages/admin/AdminUserProjects";
import AdminProjectDetail from "./pages/admin/AdminProjectDetail";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminWeekly from "./pages/admin/AdminWeekly";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminPress from "./pages/admin/AdminPress";
import AdminEmailSettings from "./pages/admin/AdminEmailSettings";
import AdminAmbassadors from "./pages/admin/AdminAmbassadors";
import AdminCommittee from "./pages/admin/AdminCommittee";
import AdminSecurity from "./pages/admin/AdminSecurity";
import AdminPublications from "./pages/admin/AdminPublications";
import AdminVideos from "./pages/admin/AdminVideos";
import AdminSocial from "./pages/admin/AdminSocial";
import AmbassadorInvite from "./pages/ambassador/AmbassadorInvite";
import AmbassadorSpace from "./pages/ambassador/AmbassadorSpace";
import PartnerRedirect from "./pages/PartnerRedirect";
import PartnerSpace from "./pages/partner/PartnerSpace";
import PartnerLanding from "./pages/PartnerLanding";
import AdminLinks from "./pages/admin/AdminLinks";
import Tracker from "./components/Tracker";

import LegalStrip from "./components/LegalStrip";

// Pages autonomes (hors PublicLayout) : le bandeau légal © / CGU / Mentions légales
// est ajouté automatiquement. Toute future page autonome doit passer par withLegal.
const withLegal = (el) => (<>{el}<LegalStrip /></>);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <AuthProvider>
        <SettingsProvider>
          <LanguageProvider>
          <InstallProvider>
          <BrowserRouter>
            <SmoothScroll>
              <Toaster position="top-left" theme="dark" richColors />
              <Tracker />
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/comment-ca-marche" element={<NavGuard settingKey="nav_how"><HowItWorks /></NavGuard>} />
                  <Route path="/challenge" element={<NavGuard settingKey="nav_challenge"><Weekly /></NavGuard>} />
                  <Route path="/applications" element={<NavGuard settingKey="nav_gallery"><Gallery /></NavGuard>} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  <Route path="/cgu" element={<Legal doc="cgu" />} />
                  <Route path="/mentions-legales" element={<Legal doc="ml" />} />
                </Route>
                <Route path="/connexion" element={withLegal(<Login />)} />
                <Route path="/inscription" element={withLegal(<Register />)} />

                <Route path="/ambassadeur" element={withLegal(<AmbassadorInvite />)} />
                <Route path="/ambassadeur/espace" element={<ProtectedRoute ambassadorOnly>{withLegal(<AmbassadorSpace />)}</ProtectedRoute>} />
                <Route path="/ambassadeur/:code" element={withLegal(<AmbassadorInvite />)} />

                <Route path="/r/:code" element={<PartnerRedirect />} />
                <Route path="/partenaire" element={withLegal(<PartnerLanding />)} />
                <Route path="/partenaire/inscription/:code" element={withLegal(<PartnerLanding />)} />
                <Route path="/partenaire/espace" element={<ProtectedRoute partnerOnly>{withLegal(<PartnerSpace />)}</ProtectedRoute>} />

                <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                  <Route index element={<Dashboard />} />
                  <Route path="deposer" element={<SubmitIdea />} />
                  <Route path="deposer/:pid" element={<SubmitIdea />} />
                  <Route path="projets" element={<MyProjects />} />
                  <Route path="projets/:pid" element={<ProjectDetail />} />
                  <Route path="profil" element={<Profile />} />
                </Route>

                <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="analyse" element={<AdminAnalytics />} />
                  <Route path="projets" element={<AdminProjects />} />
                  <Route path="groupes" element={<AdminUserProjects />} />
                  <Route path="projets/:pid" element={<AdminProjectDetail />} />
                  <Route path="utilisateurs" element={<AdminUsers />} />
                  <Route path="ambassadeurs" element={<AdminAmbassadors />} />
                  <Route path="comite" element={<AdminCommittee />} />
                  <Route path="securite" element={<AdminSecurity />} />
                  <Route path="semaine" element={<AdminWeekly />} />
                  <Route path="reglages" element={<AdminSettings />} />
                  <Route path="reglages-email" element={<AdminEmailSettings />} />
                  <Route path="presse" element={<AdminPress />} />
                  <Route path="blog" element={<AdminPublications />} />
                  <Route path="videos" element={<AdminVideos />} />
                  <Route path="reseaux" element={<AdminSocial />} />
                  <Route path="liens" element={<AdminLinks />} />                </Route>
              </Routes>
            </SmoothScroll>
          </BrowserRouter>
          </InstallProvider>
          </LanguageProvider>
        </SettingsProvider>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
